import type { ReactNode } from 'react'

import { cn } from '@/lib/utils'

export interface PageHeaderProps {
  title: string
  description?: string
  /**
   * Identifying detail that belongs beside the title rather than in the body — an account number,
   * a customer number, a cycle reference. Rendered in the monospaced face so digits line up.
   */
  reference?: string
  /** Status badges and other inline markers. */
  badges?: ReactNode
  /** Primary and secondary actions, right-aligned. Gate them with <PermissionGate>. */
  actions?: ReactNode
  className?: string
}

export function PageHeader({
  title,
  description,
  reference,
  badges,
  actions,
  className,
}: PageHeaderProps) {
  return (
    <header className={cn('flex flex-wrap items-start justify-between gap-4', className)}>
      <div className="min-w-0 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          {/*
            The name wraps on a narrow screen rather than truncating. Two words of a customer's
            name and an ellipsis identify nobody, and this heading is how an operator confirms
            they have opened the right file.
          */}
          <h1 className="text-lg font-semibold break-words text-heading md:truncate">{title}</h1>
          {reference ? (
            <span className="font-mono text-xs text-muted tabular-nums">{reference}</span>
          ) : null}
          {badges}
        </div>
        {description ? <p className="max-w-3xl text-sm text-body">{description}</p> : null}
      </div>

      {actions ? (
        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">{actions}</div>
      ) : null}
    </header>
  )
}
